import {
  buildGa4ChildBasketLineItem,
  buildGa4PrimaryCoverBasketLineItem,
  buildGa4SimpleBasketLineItem,
  collectGa4BasketProductIds,
  findParentBasketLine,
  resolveGa4CategoryProductForBasketLine,
  resolveGa4MasterProduct,
} from '@amblique/king-living/app/analytics/ga4/basket-ga4';
import {fetchGa4OptionProductMeta} from '@amblique/king-living/app/analytics/ga4/customize-atc';
import {trackAddPaymentInfo, trackAddShippingInfo, trackBeginCheckout, trackViewCart, resolveGa4BasketProductTotal} from '@amblique/king-living/app/analytics/ga4/events';
import {resolveKingGuardGa4Item} from '@amblique/king-living/app/analytics/ga4/king-guard';
import {buildSessionKey, clearSession, readSession, writeSession} from '@amblique/king-living/app/utils/session-utils';

const STRIPE_INLINE_PAYMENT_METHOD_TYPES = new Set(['apple_pay', 'card', 'google_pay', 'link']);

export const isStripeRedirectAddPaymentInfoFlow = (stripeMethodType) => (
  Boolean(stripeMethodType && !STRIPE_INLINE_PAYMENT_METHOD_TYPES.has(stripeMethodType))
);

export const stashGa4RedirectAddPaymentInfo = (orderNo, paymentType) => {
  if (!orderNo || !paymentType) {
    return;
  }

  writeSession(buildSessionKey('ga4', `redirect-add-payment-info-${orderNo}`), {
    paymentType: String(paymentType).trim(),
  });
};

const buildGa4BasketEventItemFromLine = ({
  line,
  productItems,
  productsById,
  currency,
  imageMap,
  nameMap,
}) => {
  if (!line?.productId) {
    return null;
  }

  const isChildLine = Boolean(line.c_parentProductLineItemUUId);
  const parentLine = findParentBasketLine(line, productItems) ?? line;
  const masterProduct = resolveGa4MasterProduct(parentLine, productsById);
  const categoryProduct = resolveGa4CategoryProductForBasketLine(line, productItems, productsById);

  if (isChildLine) {
    return buildGa4ChildBasketLineItem({categoryProduct, currency, imageMap, line, nameMap, productsById});
  }

  if (masterProduct) {
    return buildGa4PrimaryCoverBasketLineItem({
      categoryProduct,
      currency,
      imageMap,
      line,
      masterProduct,
      productsById,
    });
  }

  return buildGa4SimpleBasketLineItem({categoryProduct, currency, line, productsById});
};

export const buildGa4BasketEventItemsFromBasket = ({
  basket,
  currency,
  imageMap = {},
  nameMap = {},
  productsById = {},
}) => {
  const productItems = basket?.productItems ?? [];
  const items = [];

  productItems.forEach((line) => {
    const ga4Item = buildGa4BasketEventItemFromLine({
      currency,
      imageMap,
      line,
      nameMap,
      productItems,
      productsById,
    });

    if (ga4Item) {
      items.push(ga4Item);
    }

    if (line.c_parentProductLineItemUUId) {
      return;
    }

    const masterProduct = resolveGa4MasterProduct(line, productsById);
    const categoryProduct = resolveGa4CategoryProductForBasketLine(line, productItems, productsById);
    const kingGuardItem = resolveKingGuardGa4Item({
      categoryProduct,
      lineItem: line,
      masterProduct,
      productsById,
      useBasketPrice: true,
    });

    if (kingGuardItem) {
      items.push(kingGuardItem);
    }
  });

  return items;
};

const trackGa4BasketEventFromBasket = async ({
  track,
  api,
  basket,
  currency,
  customer,
  getToken,
  allowEmptyItems = false,
  // KL-1028 — reuse checkout inventory products when present so Place Order /
  // shipping funnel events do not fire a second heavy getProducts.
  productsById: preloadedProductsById = null,
}) => {
  const productItems = basket?.productItems ?? [];
  const value = resolveGa4BasketProductTotal(basket);

  if (!productItems.length) {
    if (!allowEmptyItems) {
      return false;
    }

    return track({currency, customer, items: [], value});
  }

  const {imageMap, nameMap, productsById} = await fetchGa4OptionProductMeta({
    api,
    getToken,
    includePrices: false,
    productIds: collectGa4BasketProductIds(productItems),
    productsById: preloadedProductsById,
    useBasketProductParams: true,
  });

  const items = buildGa4BasketEventItemsFromBasket({basket, currency, imageMap, nameMap, productsById});

  return track({currency, customer, items, value});
};

/** Cart flyout open — one `view_cart` per open. */
export const trackViewCartFromBasket = async (params) => trackGa4BasketEventFromBasket({
  ...params,
  allowEmptyItems: true,
  track: trackViewCart,
});

/** Checkout page load — one `begin_checkout`; same basket items/value as `view_cart`. */
export const trackBeginCheckoutFromBasket = async (params) => trackGa4BasketEventFromBasket({
  ...params,
  track: trackBeginCheckout,
});

/** After payment instrument is on basket — same items/value as `begin_checkout` + `payment_type`. */
export const trackAddPaymentInfoFromBasket = async ({paymentType, ...params}) => trackGa4BasketEventFromBasket({
  ...params,
  track: (trackParams) => trackAddPaymentInfo({...trackParams, payment_type: paymentType}),
});

/** Confirmation — fire stashed `add_payment_info` after off-site redirect checkout. */
export const trackPendingRedirectAddPaymentInfoFromOrder = async (params) => {
  const order = params?.order;
  const orderNo = order?.orderNo;
  const productItems = order?.productItems ?? [];

  if (!orderNo || !productItems.length) {
    return false;
  }

  const stashKey = buildSessionKey('ga4', `redirect-add-payment-info-${orderNo}`);
  const pending = readSession(stashKey);
  const paymentType = pending?.paymentType ? String(pending.paymentType).trim() : '';

  if (!paymentType) {
    return false;
  }

  clearSession(stashKey);

  return trackAddPaymentInfoFromBasket({
    ...params,
    basket: {productItems, productTotal: order.productSubTotal},
    paymentType,
  });
};

/** After shipping method submit — same items/value as `begin_checkout` + `shipping_tier`. */
export const trackAddShippingInfoFromBasket = async ({shippingTier, ...params}) => trackGa4BasketEventFromBasket({
  ...params,
  track: (trackParams) => trackAddShippingInfo({...trackParams, shipping_tier: shippingTier}),
});
